import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Save, Users, GraduationCap, FileText, Sliders, CalendarClock, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { ClassMappingManager } from '../components/ClassMappingManager';

const TABS = [
  { key: 'academic', label: 'Academic', icon: GraduationCap },
  { key: 'age', label: 'Age / Class Mapping', icon: CalendarClock },
  { key: 'tests', label: 'Admission Tests', icon: Sliders },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'whatsapp', label: 'WhatsApp Templates', icon: MessageSquare },
  { key: 'users', label: 'Users', icon: Users },
];
const inputCls = 'rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-action';

export default function SettingsPage() {
  const [tab, setTab] = useState('academic');
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [nu, setNu] = useState({ email: '', name: '', role: 'office' });
  const [gsConfig, setGsConfig] = useState(null);
  const [syncedTpls, setSyncedTpls] = useState([]);

  useEffect(() => {
    api.get('/settings').then((r) => setS(r.data));
    api.get('/users').then((r) => setUsers(r.data));
    api.get('/whatsapp/config-status').then((r) => setGsConfig(r.data)).catch(() => {});
    api.get('/whatsapp/templates').then((r) => setSyncedTpls(r.data.templates || [])).catch(() => {});
  }, []);

  const save = async (patch) => {
    const r = await api.put('/settings', patch);
    setS(r.data);
    toast.success('Settings saved');
  };
  const toggleTest = (name) => {
    const classes = s.classes.map((c) => c.name === name ? { ...c, test_required: !c.test_required } : c);
    setS({ ...s, classes });
  };
  const setRefDate = (session, value) => setS({ ...s, reference_dates: { ...s.reference_dates, [session]: value } });
  const toggleDoc = (name) => {
    const documents = s.documents.map((d) => d.name === name ? { ...d, required: !d.required } : d);
    setS({ ...s, documents });
  };
  const addUser = async () => {
    if (!nu.email) { toast.error('Email required'); return; }
    try {
      await api.post('/users', nu);
      const r = await api.get('/users'); setUsers(r.data);
      setNu({ email: '', name: '', role: 'office' });
      toast.success('Staff user saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save user'); }
  };
  const toggleStatus = async (email, status) => {
    try {
      await api.post('/users/status', { email, status });
      const r = await api.get('/users'); setUsers(r.data);
      toast.success(`User ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
  };
  if (!s) return <p className="text-sm text-[#94A3B8]">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#172033]">Settings</h1>
        <p className="text-sm text-[#64748B]">Configure academic sessions, tests, documents and users.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`settings-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${tab === t.key ? 'bg-navy text-white' : 'border border-[#E2E8F0] bg-white text-[#475569] hover:border-[#CBD5E1]'}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'academic' && (
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs font-semibold text-[#475569]">Current Session</span>
            <select className={inputCls} value={s.current_session} onChange={(e) => setS({ ...s, current_session: e.target.value })}>
              {s.sessions.map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-navy">Classes & ordering</p>
          <div className="flex flex-wrap gap-2">
            {s.classes.map((c) => <span key={c.name} className="rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-1 text-xs font-medium text-[#475569]">{c.order}. {c.name}</span>)}
          </div>
          <button data-testid="save-academic" onClick={() => save({ current_session: s.current_session })} className="mt-5 flex items-center gap-2 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover"><Save size={15} /> Save</button>
        </div>
      )}

      {tab === 'age' && (
        <div className="space-y-4 rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="age-class-settings">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-navy">Age reference date per session</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {s.sessions.map((sess) => (
                <label key={sess} className="block">
                  <span className="mb-1 block text-xs font-medium text-[#475569]">{sess}</span>
                  <input type="date" data-testid={`ref-date-${sess}`} className={`${inputCls} w-full`}
                    value={(s.reference_dates || {})[sess] || ''} onChange={(e) => setRefDate(sess, e.target.value)} />
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-[#64748B]">Age on the New Enquiry screen is calculated as on the selected session’s reference date.</p>
          </div>
          <button data-testid="save-age" onClick={() => save({ reference_dates: s.reference_dates })} className="flex items-center gap-2 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover"><Save size={15} /> Save Reference Dates</button>
          <div className="border-t border-[#F1F5F9] pt-4">
            <ClassMappingManager classes={s.classes.slice().sort((a, b) => a.order - b.order)} sessions={s.sessions} />
          </div>
        </div>
      )}

      {tab === 'tests' && (
        <div className="space-y-4 rounded-xl border border-[#E2E8F0] bg-white p-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block"><span className="mb-1.5 block text-xs font-semibold text-[#475569]">Pass Percentage</span>
              <input data-testid="pass-percentage" type="number" className={`${inputCls} w-full`} value={s.pass_percentage} onChange={(e) => setS({ ...s, pass_percentage: Number(e.target.value) })} /></label>
            <label className="block"><span className="mb-1.5 block text-xs font-semibold text-[#475569]">Max Marks / Subject</span>
              <input type="number" className={`${inputCls} w-full`} value={s.max_marks_per_subject} onChange={(e) => setS({ ...s, max_marks_per_subject: Number(e.target.value) })} /></label>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-navy">Classes requiring a test</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {s.classes.map((c) => (
                <button key={c.name} data-testid={`toggle-test-${c.name}`} onClick={() => toggleTest(c.name)}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${c.test_required ? 'border-[#7C3AED]/40 bg-[#EDE9FE] text-[#7C3AED]' : 'border-[#E2E8F0] bg-white text-[#64748B]'}`}>
                  {c.name}<span className="text-xs font-bold">{c.test_required ? 'Test' : 'No Test'}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><p className="mb-1 text-xs font-semibold text-[#475569]">Venues</p><p className="text-sm text-[#64748B]">{s.venues.join(', ')}</p></div>
            <div><p className="mb-1 text-xs font-semibold text-[#475569]">Rooms</p><p className="text-sm text-[#64748B]">{s.rooms.join(', ')}</p></div>
            <div><p className="mb-1 text-xs font-semibold text-[#475569]">Examiners</p><p className="text-sm text-[#64748B]">{s.examiners.join(', ')}</p></div>
          </div>
          <button data-testid="save-tests" onClick={() => save({ classes: s.classes, pass_percentage: s.pass_percentage, max_marks_per_subject: s.max_marks_per_subject })} className="flex items-center gap-2 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover"><Save size={15} /> Save</button>
        </div>
      )}

      {tab === 'documents' && (
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-navy">Document checklist</p>
          <div className="space-y-2">
            {s.documents.map((d) => (
              <button key={d.name} data-testid={`toggle-doc-${d.name}`} onClick={() => toggleDoc(d.name)}
                className="flex w-full items-center justify-between rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm hover:bg-[#F8FAFC]">
                <span className="text-[#172033]">{d.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d.required ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{d.required ? 'Required' : 'Optional'}</span>
              </button>
            ))}
          </div>
          <button data-testid="save-documents" onClick={() => save({ documents: s.documents })} className="mt-4 flex items-center gap-2 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover"><Save size={15} /> Save</button>
        </div>
      )}

      {tab === 'whatsapp' && (
        <div className="space-y-4" data-testid="whatsapp-templates-settings">
          {/* Gupshup connection status */}
          {gsConfig && (
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="gupshup-config-status">
              <div className="mb-2 flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${gsConfig.fully_configured ? 'bg-[#16A34A]' : 'bg-[#D97706]'}`} />
                <p className="text-sm font-bold text-[#172033]">Gupshup WhatsApp — {gsConfig.fully_configured ? 'Connected' : 'Not fully configured'}</p>
              </div>
              <div className="grid gap-x-6 gap-y-1 text-xs text-[#475569] sm:grid-cols-2">
                <span>API Key: <b className={gsConfig.api_key_present ? 'text-[#16A34A]' : 'text-[#DC2626]'}>{gsConfig.api_key_present ? 'Present (secure)' : 'Missing — add GUPSHUP_API_KEY secret'}</b></span>
                <span>App Name: <b>{gsConfig.app_name || '—'}</b></span>
                <span>Source Phone: <b>{gsConfig.source_phone || '—'}</b></span>
                <span>Enquiry Template: <b className={gsConfig.enquiry_template_id_present ? 'text-[#16A34A]' : 'text-[#D97706]'}>{gsConfig.enquiry_template_id_present ? 'Configured' : 'Pending approval / not set'}</b></span>
                <span className="sm:col-span-2 break-all">Webhook URL: <b>{gsConfig.webhook_url || '—'}</b></span>
              </div>
            </div>
          )}

          {/* Gupshup-synced templates (read-only) */}
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="synced-templates">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-bold text-[#172033]">WhatsApp Templates — synced from Gupshup</p>
              <a data-testid="open-communications-link" href="/communications" className="text-xs font-semibold text-action hover:underline">Open Communications →</a>
            </div>
            <p className="mb-3 text-xs text-[#64748B]">Templates are created and approved in your Gupshup app and sync here automatically — Gupshup is the single source of truth. Use Communications → WhatsApp to preview/send and Communications → Automation to map workflows.</p>
            {syncedTpls.length === 0 ? (
              <p className="rounded-lg bg-[#F8FAFC] p-3 text-xs text-[#94A3B8]" data-testid="synced-empty">No approved templates synced yet. Approve a template in Gupshup, then click "Refresh Templates" in Communications.</p>
            ) : (
              <div className="space-y-2">
                {syncedTpls.map((t) => (
                  <div key={t.gupshupTemplateId} data-testid={`synced-template-${t.name}`} className="flex items-center justify-between rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-semibold text-[#172033]">{t.name}</span>
                      <span className="ml-2 text-xs text-[#64748B]">{t.category} · {t.languageCode} · {t.templateType}</span>
                    </div>
                    <span className="rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-bold uppercase text-[#16A34A]">{t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-navy">Add / update user role</p>
            <div className="grid gap-3 sm:grid-cols-4">
              <input data-testid="user-email" className={inputCls} placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} />
              <input className={inputCls} placeholder="Name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} />
              <select data-testid="user-role" className={inputCls} value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
                {['office', 'rector'].map((r) => <option key={r}>{r}</option>)}
              </select>
              <button data-testid="add-user" onClick={addUser} className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dark">Save User</button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="border-b border-[#E2E8F0] bg-[#F8FAFC] text-left">{['Name', 'Email', 'Role', 'Status', 'Last Login', 'Action'].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy">{h}</th>)}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.email} data-testid={`user-row-${u.email}`} className="border-b border-[#F1F5F9] last:border-0">
                    <td className="px-4 py-2.5 font-semibold text-[#172033]">{u.name}</td>
                    <td className="px-4 py-2.5 text-[#475569]">{u.email}</td>
                    <td className="px-4 py-2.5"><span className="rounded-full bg-navy/5 px-2 py-0.5 text-xs font-semibold uppercase text-navy">{u.role}</span></td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${(u.status || 'active') === 'active' ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{u.status || 'active'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#94A3B8]">{u.last_login ? new Date(u.last_login).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}</td>
                    <td className="px-4 py-2.5">
                      {(u.status || 'active') === 'active' ? (
                        <button data-testid={`deactivate-${u.email}`} onClick={() => toggleStatus(u.email, 'inactive')} className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-[#DC2626] hover:bg-[#FEE2E2]">Deactivate</button>
                      ) : (
                        <button data-testid={`reactivate-${u.email}`} onClick={() => toggleStatus(u.email, 'active')} className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-[#16A34A] hover:bg-[#DCFCE7]">Reactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
